import { Avatar, IconButton } from "@ameelio/ui";
import CloseIcon from "@mui/icons-material/Close";
import MicIcon from "@mui/icons-material/Mic";
import MicOffOutlinedIcon from "@mui/icons-material/MicOffOutlined";
import { Box, Card, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { CallParticipant, ParticipantType } from "../../api/graphql.ts";
import { getPeersOrdered, peerCanModerate } from "../utils";
import { Peer } from "./types/ConnectionInfo";

type ParticipantCardProps = {
  currentParticipant: Pick<CallParticipant, "externalId" | "type">;
  meetingUser: {
    id?: string;
    fullName: string;
  };
  isHost: boolean;
  muteAudio?: () => void;
  unmuteAudio?: () => void;
  audioMuted?: boolean;
};

function ParticipantCard({
  currentParticipant,
  meetingUser,
  isHost,
  muteAudio,
  unmuteAudio,
  audioMuted,
}: ParticipantCardProps) {
  const { t } = useTranslation();
  return (
    <Card variant="outlined" sx={{ my: 2, px: { xs: 1, sm: 2 }, py: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          maxWidth: "100%",
          overflow: "auto",
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: "center",
          }}
        >
          <Avatar contact={meetingUser} />
          <Box>
            {meetingUser.fullName}{" "}
            {currentParticipant.externalId === meetingUser.id
              ? t("(you)")
              : isHost && currentParticipant.type === ParticipantType.User
                ? t("(co-host)")
                : isHost && currentParticipant.type === ParticipantType.Inmate
                  ? t("(host)")
                  : null}
          </Box>
        </Stack>
        {audioMuted != null && (
          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: "center",
            }}
          >
            {unmuteAudio && muteAudio ? (
              audioMuted ? (
                <IconButton
                  tooltip
                  ariaLabel={t("Unmute")}
                  onClick={unmuteAudio}
                >
                  <MicOffOutlinedIcon />
                </IconButton>
              ) : (
                <IconButton tooltip ariaLabel={t("Mute")} onClick={muteAudio}>
                  <MicIcon />
                </IconButton>
              )
            ) : audioMuted ? (
              <IconButton ariaLabel={t("Unmute")} disabled>
                <MicOffOutlinedIcon />
              </IconButton>
            ) : (
              <IconButton ariaLabel={t("Mute")} disabled>
                <MicIcon />
              </IconButton>
            )}
          </Stack>
        )}
      </Box>
    </Card>
  );
}

type Props = {
  currentParticipant: Pick<CallParticipant, "externalId" | "type">;
  isHost: boolean;
  peers: Peer[];
  meetingUsers: {
    [id: string]: { fullName: string };
  };
  remoteAudioUnmute?: (id: string) => void;
  remoteAudioMute?: (id: string) => void;
  toggleAudio: () => void;
  audioPaused: boolean;
  onClose: () => void;
};

export default function CallParticipants({
  currentParticipant,
  isHost,
  peers,
  remoteAudioUnmute,
  remoteAudioMute,
  toggleAudio,
  audioPaused,
  meetingUsers,
  onClose,
}: Props) {
  const { t } = useTranslation();

  // Put hosts at the top of the list to mirror order in page
  const presentPeers = getPeersOrdered(peers).filter(
    (x) =>
      x.user.id in meetingUsers && x.user.id !== currentParticipant.externalId
  );
  const absentPeers = Object.keys(meetingUsers)
    .filter(
      (id) =>
        !peers.some((peer) => peer.user.id === id) &&
        currentParticipant.externalId !== id
    )
    .map((id) => ({ id, ...meetingUsers[id] }))
    .sort((a, b) => a.fullName.localeCompare(b.fullName));

  function askUnmute(peer: { user: { id: string } }) {
    if (remoteAudioUnmute) remoteAudioUnmute(peer.user.id);
  }

  function askMute(peer: { user: { id: string } }) {
    if (remoteAudioMute) remoteAudioMute(peer.user.id);
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: 1,
        overflow: "auto",
      }}
    >
      <Box sx={{ display: "flex", padding: 2 }}>
        <Typography variant="h3" component="div" sx={{ flexGrow: 1 }}>
          {t("Participants")}
        </Typography>
        <IconButton size="small" onClick={onClose} ariaLabel={t("Close")}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ p: 2 }}>
        <Box>
          <Typography variant="overline">
            {t("In Call")} ({presentPeers.length + 1})
          </Typography>
        </Box>
        <ParticipantCard
          currentParticipant={currentParticipant}
          meetingUser={meetingUsers[currentParticipant.externalId]}
          isHost={isHost}
          muteAudio={isHost ? toggleAudio : undefined}
          unmuteAudio={isHost ? toggleAudio : undefined}
          audioMuted={audioPaused}
        />
        {presentPeers.map((peer) => {
          const consideredAudioMuted = peer.consumers.audio?.paused;

          const peerIsModerator = peerCanModerate(peer);
          const canModify = isHost && !peerIsModerator;

          return (
            <ParticipantCard
              currentParticipant={currentParticipant}
              key={peer.user.id}
              meetingUser={meetingUsers[peer.user.id]}
              isHost={peerIsModerator}
              muteAudio={canModify ? () => askMute(peer) : undefined}
              unmuteAudio={canModify ? () => askUnmute(peer) : undefined}
              audioMuted={consideredAudioMuted}
            />
          );
        })}
        <Typography variant="overline">
          {t("Not Present")} ({absentPeers.length})
        </Typography>
        {absentPeers.map((meetingUser) => (
          <ParticipantCard
            currentParticipant={currentParticipant}
            key={meetingUser.id}
            meetingUser={meetingUser}
            isHost={false}
            muteAudio={undefined}
            unmuteAudio={undefined}
            audioMuted={undefined}
          />
        ))}
      </Box>
    </Box>
  );
}
